import { Root as CheckboxRoot, Indicator } from "@radix-ui/react-checkbox";
import type { ComponentPropsWithoutRef } from "react";
import { cn } from "../lib/utils";
import Icon from "./icon";

interface CheckboxProps extends ComponentPropsWithoutRef<typeof CheckboxRoot> {
  label?: string;
  description?: string;
  error?: boolean;
  required?: boolean;
  checked?: boolean | "indeterminate";
}

//
const Checkbox = ({
  className,
  label,
  description,
  error,
  required,
  id,
  ref,
  ...props
}: CheckboxProps & { ref?: React.Ref<HTMLButtonElement> }) => (
  <div className={cn("items-center flex space-x-2", className)}>
    <CheckboxRoot
      id={id}
      ref={ref}
      // biome-ignore lint/a11y/useSemanticElements: We use our own semantic elements
      role="checkbox"
      aria-checked={
        props.checked === "indeterminate" ? "mixed" : props.checked || false
      }
      className={cn(
        "peer grid size-5 shrink-0 rounded border-2 border-gray-200 focus-visible:outline  bg-white",
        props.disabled
          ? "disabled:cursor-not-allowed disabled:border-gray-100 disabled:hover:border-gray-100 disabled:bg-gray-50"
          : "hover:border-secondary data-[state=indeterminate]:bg-secondary data-[state=indeterminate]:border-none data-[state=checked]:border-none data-[state=checked]:bg-secondary",
        error && "border-red-500",
      )}
      {...props}
    >
      <Indicator className="text-current m-auto" aria-hidden="true">
        <Icon
          icon={props.checked === "indeterminate" ? "minus" : "check"}
          size={12}
          color={
            props.disabled
              ? "rgb(173 181 195 / var(--tw-text-opacity, 1))"
              : "white"
          }
        />
      </Indicator>
    </CheckboxRoot>
    {(label || description) && (
      <div className="leading-none">
        {label && (
          <label
            htmlFor={id}
            className={cn(
              "text-sm font-title leading-5",
              "peer-disabled:cursor-not-allowed peer-disabled:text-gray-200",
            )}
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        {description && (
          <p className="text-sm text-gray-500" id={`${id}-description`}>
            {description}
          </p>
        )}
      </div>
    )}
  </div>
);

Checkbox.displayName = CheckboxRoot.displayName;

export { Checkbox };
